import type { FieldSceneDefinition, SceneMaterialDefinition, Vec3Tuple } from './fieldSchema';

export interface LayoutBox {
  name: string;
  center: Vec3Tuple;
  dimensions: Vec3Tuple;
}

export interface ObjectLayout {
  name: string;
  position: Vec3Tuple;
  yaw: number;
  material: SceneMaterialDefinition;
  boxes: LayoutBox[];
}

export const deriveFieldObjectLayouts = (field: FieldSceneDefinition): ObjectLayout[] => {
  const hangar = field.hangar;
  const [width, depth, height] = hangar.dimensions;
  const thickness = hangar.wallThickness;
  const allHangarBoxes: LayoutBox[] = [
    {
      name: 'wall-north',
      center: [0, depth / 2 - thickness / 2, height / 2],
      dimensions: [width, thickness, height],
    },
    {
      name: 'wall-south',
      center: [0, -depth / 2 + thickness / 2, height / 2],
      dimensions: [width, thickness, height],
    },
    {
      name: 'wall-east',
      center: [width / 2 - thickness / 2, 0, height / 2],
      dimensions: [thickness, depth, height],
    },
    {
      name: 'wall-west',
      center: [-width / 2 + thickness / 2, 0, height / 2],
      dimensions: [thickness, depth, height],
    },
    {
      name: 'roof',
      center: [0, 0, height - thickness / 2],
      dimensions: [width, depth, thickness],
    },
  ];
  const hangarBoxes = allHangarBoxes.filter((box) => box.name !== `wall-${hangar.openFace}`);

  const layouts: ObjectLayout[] = [
    {
      name: 'hangar',
      position: hangar.position,
      yaw: 0,
      material: hangar.material,
      boxes: hangarBoxes,
    },
  ];

  for (const gate of field.gates) {
    const [innerWidth, innerHeight] = gate.innerSize;
    const thickness = gate.postThickness;
    const postOffset = innerWidth / 2 + thickness / 2;
    layouts.push({
      name: `gate:${gate.id}`,
      position: gate.position,
      yaw: gate.orientation,
      material: gate.material,
      boxes: [
        {
          name: 'post-left',
          center: [-postOffset, 0, 0],
          dimensions: [thickness, thickness, innerHeight],
        },
        {
          name: 'post-right',
          center: [postOffset, 0, 0],
          dimensions: [thickness, thickness, innerHeight],
        },
        {
          name: 'top',
          center: [0, 0, innerHeight / 2 + thickness / 2],
          dimensions: [innerWidth + 2 * thickness, thickness, thickness],
        },
      ],
    });
  }

  for (const crate of field.crates) {
    layouts.push({
      name: `crate:${crate.id}`,
      position: crate.position,
      yaw: crate.orientation,
      material: crate.material,
      boxes: [
        { name: 'body', center: [0, 0, crate.dimensions[2] / 2], dimensions: crate.dimensions },
      ],
    });
  }

  return layouts;
};
